// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import MiniCartActionButtons from "@bg/App/Components/XngMiniCart/XngMiniCartActions";
import MiniCartLine from "@bg/App/Components/XngMiniCart/XngMiniCartLine";
import { minicartStyles } from "@bg/App/Components/XngMiniCart/XngMiniCartStyles";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import {
    getCurrentCartState,
    hasQuoteRequiredProducts,
    isCartEmpty,
} from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import removeCartLine from "@insite/client-framework/Store/Pages/Cart/Handlers/RemoveCartLine";
import updateCartLine from "@insite/client-framework/Store/Pages/Cart/Handlers/UpdateCartLine";
import translate from "@insite/client-framework/Translate";
import { CartLineModel } from "@insite/client-framework/Types/ApiModels";
import LoadingSpinner from "@insite/mobius/LoadingSpinner";
import Typography from "@insite/mobius/Typography";
import React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface MiniCartHeadingProps {
    count?: number;
    subTotal?: string;
    hasQuoteRequiredProducts?: boolean;
}

const MiniCartHeading = ({ count, subTotal, hasQuoteRequiredProducts }: MiniCartHeadingProps) => {
    return (
        <StyledWrapper {...minicartStyles.miniCartHeadingStyles}>
            <StyledWrapper {...minicartStyles.miniCartHeadingTotalStyles}>
                <StyledWrapper
                    css={css`
                        display: flex;
                        min-width: 240px;
                    `}
                >
                    <Typography
                        size="28px"
                        lineHeight="22px"
                        weight="bold"
                        css={css`
                            margin-right: 5px;
                        `}
                    >
                        {translate("Cart Summary")}
                    </Typography>
                    <Typography className="total-items">{count || 0}</Typography>
                </StyledWrapper>
                <StyledWrapper>
                    <Typography as="strong" size="18px" lineHeight="22px">
                        {translate("Subtotal:")}{" "}
                    </Typography>
                    <Typography {...minicartStyles.subTotalText}>{subTotal || 0}</Typography>
                </StyledWrapper>
            </StyledWrapper>
        </StyledWrapper>
    );
};

/**
 *  Mini-cart component Main entrance
 */

const mapStateToProps = (state: ApplicationState) => {
    const cart = getCurrentCartState(state).value;
    const pageType = getCurrentPage(state).type;
    const shouldDisableQtyInput = pageType === "CheckoutReviewAndSubmitPage";
    const customSettings = getSettingsCollection(state).customSettings as any;

    return {
        cart,
        isLoading: getCurrentCartState(state).isLoading,
        isEmpty: isCartEmpty(cart),
        hasQuoteRequiredProducts: hasQuoteRequiredProducts(cart),
        isRemovingCartLine: state.pages.cart?.hasOwnProperty("isRemovingCartLine")
            ? state.pages.cart?.isRemovingCartLine
            : null,
        shouldDisableQtyInput: shouldDisableQtyInput || !cart?.canModifyOrder,
        isAuth: getSession(state).isAuthenticated,
        customSettings,
    };
};

const mapDispatchToProps = {
    updateCartLine,
    removeCartLine,
};

type OwnProps = ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & { closeOnClick: any };

const XngMiniCart: React.FC<OwnProps> = ({
    cart,
    updateCartLine,
    removeCartLine,
    closeOnClick,
    isEmpty,
    isRemovingCartLine,
    hasQuoteRequiredProducts,
    isLoading,
    shouldDisableQtyInput,
    isAuth,
    customSettings,
}) => {
    const [showSpinner, setShowSpinner] = React.useState(false);
    const count = cart?.totalCountDisplay;
    const subTotal = cart?.orderSubTotalDisplay;

    const [removedCartId, setRemovedCartId] = React.useState("");

    const [showPricing, setShowPricing] = React.useState(false);

    React.useEffect(() => {
        if (
            customSettings &&
            customSettings.postalCodeLocationFinder.toLowerCase() === "true" &&
            !window.localStorage.getItem("postalCodeID")?.length &&
            !isAuth
        ) {
            setShowPricing(false);
        } else {
            setShowPricing(true);
        }
    }, []);

    const handleRemoveLineWithSpinner = (cartLineId: string) => {
        setRemovedCartId(cartLineId);
        removeCartLine({ cartLineId });
    };

    React.useEffect(() => {
        if (isRemovingCartLine && isRemovingCartLine?.hasOwnProperty(removedCartId)) {
            setShowSpinner(true);
        } else {
            setShowSpinner(false);
        }
    }, [isRemovingCartLine]);

    const showNA = !showPricing || cart?.cartNotPriced;

    return (
        <StyledWrapper {...minicartStyles.miniCartContainerStyles}>
            <MiniCartHeading
                count={count}
                subTotal={showNA ? translate("N/A") : subTotal}
                hasQuoteRequiredProducts={hasQuoteRequiredProducts}
            />

            <StyledWrapper {...minicartStyles.fancyOverflowStyles} className={isEmpty ? "empty-cart" : ""}>
                {(showSpinner || isLoading) && (
                    <StyledWrapper {...minicartStyles.spinnerWrapper}>
                        <LoadingSpinner></LoadingSpinner>
                    </StyledWrapper>
                )}
                <StyledWrapper className="inner-wrapper">
                    {cart && cart?.cartLines?.length ? (
                        cart.cartLines.map((cartLine: CartLineModel) => {
                            return (
                                <MiniCartLine
                                    setShowSpinner={setShowSpinner}
                                    cartType={cart.type}
                                    cartLine={cartLine}
                                    closeOnClick={closeOnClick}
                                    key={cartLine.id}
                                    removeCartLine={handleRemoveLineWithSpinner}
                                    updateCartLine={updateCartLine}
                                    shouldDisableQtyInput={shouldDisableQtyInput}
                                />
                            );
                        })
                    ) : (
                        <StyledWrapper>
                            <Typography
                                css={css`
                                    display: flex;
                                    align-items: center;
                                    justify-content: center;
                                    height: 100px;
                                `}
                                as="p"
                            >
                                {siteMessage("Custom_Empty_Mini_Cart_Msg")}
                            </Typography>
                        </StyledWrapper>
                    )}
                </StyledWrapper>
            </StyledWrapper>
            <MiniCartActionButtons closeOnClick={closeOnClick} />
        </StyledWrapper>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(XngMiniCart);
